// export default Navbar;

import React, { useState, useEffect, useRef } from "react";
import "../styles/Navbar.css";
import { Link, useNavigate } from "react-router-dom";
import logo from "../images/logo.png";
import { ChevronDown, LogOut, User } from "lucide-react";
import {
  isUserAuthenticated,
  getCurrentUser,
  clearUserAuth,
  redirectToCorrectDashboard,
} from "../utils/authUtils";
import { toast } from "react-toastify";

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const navbarRef = useRef(null);
  const navigate = useNavigate();

  // Check authentication status on mount and periodically
  useEffect(() => {
    const checkAuth = () => {
      const authStatus = isUserAuthenticated();
      setIsAuthenticated(authStatus);

      if (authStatus) {
        const user = getCurrentUser();
        setCurrentUser(user);
      } else {
        setCurrentUser(null);
      }
    };

    // Check on mount
    checkAuth();

    // Check periodically (every 2 seconds) to detect auth changes
    const authCheckInterval = setInterval(checkAuth, 2000);

    return () => clearInterval(authCheckInterval);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navbarRef.current && !navbarRef.current.contains(event.target)) {
        setActiveDropdown(null);
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setActiveDropdown(null); // Close any open dropdowns when toggling menu
  };

  const handleDropdown = (menu) => {
    setActiveDropdown(activeDropdown === menu ? null : menu);
  };

  const closeAllMenus = () => {
    setActiveDropdown(null);
    setIsMenuOpen(false);
  };

  const handleLogout = () => {
    closeAllMenus();
    clearUserAuth(navigate);
    toast.success("Logged out successfully");
  };

  const handleDashboardClick = () => {
    closeAllMenus();
    if (currentUser && currentUser.role) {
      redirectToCorrectDashboard(currentUser.role, navigate);
    }
  };

  const navItems = [
    {
      title: "Home",
      link: "/",
      hasDropdown: false,
    },
    {
      title: "Study Destinations",
      hasDropdown: true,
      dropdownItems: [
        { title: "Canada", link: "/canada" },
        { title: "United Kingdom", link: "/uk" },
        { title: "United States", link: "/us" },
        { title: "Australia", link: "/aus" },
        { title: "New Zealand", link: "/nz" },
        { title: "Germany", link: "/germany" },
        { title: "France", link: "/france" },
        { title: "View All Destinations", link: "/destinations" },
      ],
    },
    {
      title: "Services",
      hasDropdown: true,
      dropdownItems: [
        { title: "For Students", link: "/plan-studies" },
        // { title: "For Institutions", link: "/track-progress" },
        { title: "For Recruitment Partners", link: "/resources" },
        { title: "All Services", link: "/services" },
      ],
    },
    {
      title: "About",
      hasDropdown: true,
      dropdownItems: [
        { title: "About Us", link: "/about" },
        { title: "Our Partners", link: "/associates-dynamic" },
        { title: "Blog", link: "/blog" },
        { title: "Support", link: "/support" },
      ],
    },
  ];

  return (
    <nav className="navbar" ref={navbarRef}>
      <div className="navbar-container">
        <div className="navbar-logo">
          <Link to="/" onClick={closeAllMenus}>
            <img src={logo} alt="PlanStudies Logo" className="logo-img" />
          </Link>
        </div>

        <div className={`navbar-menu ${isMenuOpen ? "active" : ""}`}>
          <ul className="navbar-links">
            {navItems.map((item, index) => (
              <li key={index} className="nav-item">
                {item.hasDropdown ? (
                  <div className="dropdown">
                    <button
                      className="nav-link dropdown-toggle"
                      onClick={() => handleDropdown(item.title)}
                    >
                      {item.title}
                      <ChevronDown
                        size={16}
                        className={`dropdown-icon ${
                          activeDropdown === item.title ? "rotated" : ""
                        }`}
                      />
                    </button>
                    <div
                      className={`dropdown-menu ${
                        activeDropdown === item.title ? "show" : ""
                      }`}
                    >
                      {item.dropdownItems.map((dropItem, dropIndex) => (
                        <Link
                          key={dropIndex}
                          to={dropItem.link}
                          className="dropdown-item"
                          onClick={closeAllMenus}
                        >
                          {dropItem.title}
                        </Link>
                      ))}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.link}
                    className="nav-link"
                    onClick={closeAllMenus}
                  >
                    {item.title}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Mobile buttons inside menu */}
          <div className="navbar-btns mobile-btns">
            {isAuthenticated ? (
              <>
                <button
                  onClick={handleDashboardClick}
                  className="btn-create"
                  title={
                    currentUser?.role
                      ? `${currentUser.role} Dashboard`
                      : "Dashboard"
                  }
                >
                  <User
                    size={16}
                    style={{ marginRight: "4px", display: "inline" }}
                  />
                  Dashboard
                </button>
                <button onClick={handleLogout} className="btn-login">
                  <LogOut
                    size={16}
                    style={{ marginRight: "4px", display: "inline" }}
                  />
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/create" onClick={closeAllMenus}>
                  <button className="btn-create">Create Account</button>
                </Link>
                <Link to="/login" onClick={closeAllMenus}>
                  <button className="btn-login">Log In</button>
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Desktop buttons */}
        <div className="navbar-btns desktop-btns">
          {isAuthenticated ? (
            <>
              <button
                onClick={handleDashboardClick}
                className="btn-create"
                title={
                  currentUser?.role
                    ? `${currentUser.role} Dashboard`
                    : "Dashboard"
                }
              >
                <User
                  size={16}
                  style={{ marginRight: "4px", display: "inline" }}
                />
                Dashboard
              </button>
              <button onClick={handleLogout} className="btn-login">
                <LogOut
                  size={16}
                  style={{ marginRight: "4px", display: "inline" }}
                />
                Logout
              </button>
            </>
          ) : (
            <>
              <Link to="/create">
                <button className="btn-create">Create Account</button>
              </Link>
              <Link to="/login">
                <button className="btn-login">Log In</button>
              </Link>
            </>
          )}
          <a
            href="https://wa.me/+916357091029"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
          >
            WhatsApp
          </a>
        </div>

        <div className="menu-toggle" onClick={toggleMenu}>
          <span className={`bar ${isMenuOpen ? "active" : ""}`}></span>
          <span className={`bar ${isMenuOpen ? "active" : ""}`}></span>
          <span className={`bar ${isMenuOpen ? "active" : ""}`}></span>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
